/* global debounce */

/* DC search helpers (2026-09-22, precision + SKU 2026-09-29) - identical copy in assets/dc-search.js. */
window.dcSearchIntent = window.dcSearchIntent || (() => {
  const patterns = {
    used: /\b(used|pre[\s-]?owned|second[\s-]?hand|refurbished|open[\s-]?box)\b/i,
    packages: /\b(packages?|bundles?)\b/i
  };
  const stopWords = new Set(['a', 'an', 'and', 'the', 'for', 'with', 'of', 'in', 'on', 'to', 'by', 'or', 'not', 'w']);
  return {
    detect(term) {
      if (patterns.used.test(term)) return 'used';
      if (patterns.packages.test(term)) return 'packages';
      return 'products';
    },
    strip(term, tab) {
      const re = patterns[tab];
      if (!re) return term;
      const stripped = term.replace(new RegExp(re.source, 'gi'), ' ').replace(/\s+/g, ' ').trim();
      return stripped || term;
    },
    /**
     * One "word" that looks like a SKU: a digit plus SKU punctuation, or 5+ digits
     * (96.102.110, SPRO-24.714.130+LN, TC-407, 48211414030) - not model names like mk25, d530, x50i.
     */
    isSkuLike(term) {
      const t = (term || '').trim();
      if (t.length < 4 || !/\d/.test(t) || !/^[A-Za-z0-9][A-Za-z0-9.\-+/_#]*$/.test(t)) return false;
      return /[.\-+/_#]/.test(t) || (t.match(/\d/g) || []).length >= 5;
    },
    /**
     * "Every word must be in the title or the brand": (title:w1 OR vendor:w1) (title:w2 OR vendor:w2).
     * Shopify ANDs fielded terms (plain words are ORed), so this only returns close matches.
     * A SKU-looking query becomes (variants.sku:X OR title:X OR vendor:X). Returns '' when nothing is usable.
     */
    preciseQuery(term) {
      const sku = (term || '').trim();
      // A SKU/model number: its SKU tokens or title (plain words next to a -tag: exclusion match SKUs loosely).
      if (this.isSkuLike(sku)) return `(variants.sku:${sku} OR title:${sku} OR vendor:${sku})`;
      // Shopify keeps dots and apostrophes inside a word ("4.0", "7.5mm", "4080.03", "n'roll" are one
      // token) and splits on spaces, hyphens and other punctuation - split the same way.
      const words = (term || '')
        .toLowerCase()
        .replace(/’/g, "'")
        .split(/[^\p{L}\p{N}.']+/u)
        .map((w) => w.replace(/^[.']+|[.']+$/g, ''))
        .filter((w) => w && !stopWords.has(w));
      const unique = [...new Set(words)].slice(0, 8);
      return unique.map((w) => `(title:${w} OR vendor:${w})`).join(' ');
    },
    /** Renders sections/dc-search-pins for q. Resolves to { count, items: [li, ...] }. */
    async fetchPins(searchUrl, q) {
      const url = new URL(searchUrl || '/search', window.location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', q);
      url.searchParams.set('section_id', 'dc-search-pins');
      const response = await fetch(url.toString());
      if (!response.ok) return { count: 0, items: [] };
      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();
      const root = tmpl.content.querySelector('[data-dc-pins]');
      if (!root) return { count: 0, items: [] };
      return {
        count: parseInt(root.dataset.count, 10) || 0,
        items: [...root.querySelectorAll('li[data-dc-handle]')]
      };
    },
    /**
     * For a SKU-looking query: { url, tab } when exactly one product has a variant with that exact SKU,
     * else null (the caller then searches normally). Two lookups in parallel: variants.sku: and the plain
     * word (a few SKUs aren't reachable through variants.sku:).
     */
    async resolveSku(searchUrl, term) {
      const sku = (term || '').trim();
      const results = await Promise.all([
        this.fetchPins(searchUrl, `variants.sku:${sku}`),
        this.fetchPins(searchUrl, sku)
      ]);
      const exact = new Map();
      results.forEach((res) => res.items.forEach((li) => {
        if (li.dataset.dcExactUrl && !exact.has(li.dataset.dcHandle)) exact.set(li.dataset.dcHandle, li);
      }));
      if (exact.size !== 1) return null;
      const [li] = exact.values();
      return { url: li.dataset.dcExactUrl, tab: li.dataset.dcKind };
    },
    /** Rejects after ms so a slow lookup never blocks a search. */
    withTimeout(promise, ms) {
      return Promise.race([promise, new Promise((resolve, reject) => { setTimeout(() => reject(new Error('timeout')), ms); })]);
    }
  };
})();

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.form = this.querySelector('form.search');
      try {
        this.scopes = JSON.parse(this.getAttribute('data-dc-scopes') || '{}');
      } catch (e) {
        this.scopes = {};
      }
      this.tabOrder = ['products', 'used', 'packages', 'collections', 'articles_and_pages', 'pages', 'articles'];
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit. Sends the customer to the right results tab (Products by default,
     * Used & Open Box / Packages when the words say so or when that dropdown tab is active).
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      const query = this.getQuery();
      if (
        !query.length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
        return;
      }

      // Legacy "product type" dropdown path: leave the native submit alone.
      if (this.productTypeInput && this.productTypeInput.value !== '') return;

      const url = this.buildSearchUrl(query);
      if (!url) return;
      evt.preventDefault();

      // A SKU: go straight to the product when exactly one variant has it. Not when the customer clicked
      // "View all" in a dropdown tab they chose - that click means "show me this tab".
      const helper = window.dcSearchIntent;
      const viewAll = evt.submitter && evt.submitter.classList.contains('predictive-search__view-all');
      if (!viewAll && helper.isSkuLike(query)) {
        // The dropdown for this exact text already shows an exact-SKU row: use it, no extra lookup.
        const row = this.renderedQuery === query ? this.querySelector('li[data-dc-sku-match]') : null;
        if (row) {
          window.location.assign(row.dataset.dcSkuMatch);
          return;
        }
        const action = this.form.getAttribute('action') || '/search';
        helper.withTimeout(helper.resolveSku(action, query), 2500)
          .then((hit) => window.location.assign(hit && hit.url ? hit.url : url))
          .catch(() => window.location.assign(url));
        return;
      }

      window.location.assign(url);
    }

    /**
     * Builds the scoped results-page URL for a query.
     * @param {string} query - Search query.
     * @param {string} [forcedTab] - Tab to use instead of the dropdown tab / detected intent.
     * @returns {string|null}
     */
    buildSearchUrl(query, forcedTab) {
      const action = this.form.getAttribute('action') || '/search';
      let tab = forcedTab || null;
      let term = query;

      if (!tab && this.hasAttribute('open')) {
        const activeTab = this.querySelector('[role="tab"][aria-selected="true"]');
        const match = activeTab && activeTab.id.match(/-(products|used|packages|collections|articles_and_pages|pages|articles)-tab$/);
        if (match) [, tab] = match;
      }

      if (!tab) {
        tab = window.dcSearchIntent.detect(query);
        term = window.dcSearchIntent.strip(query, tab);
      } else if (tab === window.dcSearchIntent.detect(query)) {
        // "open box bcd" on the Used tab: the intent words are the tab, not search terms.
        term = window.dcSearchIntent.strip(query, tab);
      }

      const params = new URLSearchParams();
      params.set('options[prefix]', 'last');

      if (tab === 'articles_and_pages' || tab === 'pages' || tab === 'articles') {
        params.set('type', 'article,page');
        params.set('q', term);
      } else {
        const key = tab === 'collections' ? 'products' : tab;
        const scope = this.scopes[key];
        if (!scope) return null;
        params.set('type', 'product');
        params.set('q', `${scope} ${term}`);
      }

      return `${action}?${params.toString()}`;
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        // Drop any batch still in flight so it can't reopen the dropdown.
        this.latestRequestKey = null;
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Builds the query string for one predictive-search section request.
     * @param {string} q - Full q value (scope + term).
     * @param {string} types - resources[type] value.
     * @returns {string}
     */
    buildRequestParams(q, types) {
      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let params = `q=${encodeURIComponent(q)}`;
      params += `&${encodeURIComponent('resources[type]')}=${types}`;
      params += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      params += `&${encodeURIComponent('resources[limit_scope]')}=${theme.settings.pSearchLimitScope}`;
      params += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      params += '&section_id=predictive-search';
      return params;
    }

    /**
     * Fetches one rendered predictive-search section and returns its inner HTML.
     * @param {string} params - Query string.
     * @returns {Promise<string>}
     */
    async fetchSection(params) {
      const response = await fetch(`${theme.routes.predictiveSearch}?${params}`);
      if (!response.ok) throw new Error(response.status);
      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();
      const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
      return resultsEl ? resultsEl.innerHTML : '';
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      const productType = this.productTypeInput ? this.productTypeInput.value : '';
      const queryKey = `${productType}|${searchTerm}`.toLowerCase();
      // Mark this as the latest query first, so an older batch still in flight can't overwrite it.
      this.latestRequestKey = queryKey;
      if (this.cachedResults[queryKey]) {
        this.renderedQuery = searchTerm;
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        let resultsMarkup;

        if (productType !== '' || !this.scopes.products) {
          // Legacy single-request path (product type dropdown, or scopes missing).
          const q = productType !== '' ? `product_type:${productType} AND ${searchTerm}` : searchTerm;
          resultsMarkup = await this.fetchSection(this.buildRequestParams(q, 'query,product,collection,page,article'));
        } else {
          const requests = {
            plain: this.buildRequestParams(searchTerm, 'query,collection,page,article'),
            products: this.buildRequestParams(`${this.scopes.products} ${searchTerm}`, 'product'),
            used: this.buildRequestParams(`${this.scopes.used} ${searchTerm}`, 'product'),
            packages: this.buildRequestParams(`${this.scopes.packages} ${searchTerm}`, 'product')
          };
          // Best matches: every word in the title/brand, within the tab the words ask for (Products by
          // default). A SKU is looked up across all tabs. Items carry data-dc-kind for the merge.
          const helper = window.dcSearchIntent;
          const intent = helper.detect(searchTerm);
          const words = helper.strip(searchTerm, intent);
          const precise = helper.preciseQuery(words);
          if (precise) {
            const q = helper.isSkuLike(words) ? precise : `${this.scopes[intent] || this.scopes.products} ${precise}`;
            requests.precise = this.buildRequestParams(q, 'product');
          }
          const keys = Object.keys(requests);
          const responses = await Promise.all(keys.map((key) => this.fetchSection(requests[key]).catch(() => '')));
          const parts = {};
          keys.forEach((key, i) => { parts[key] = responses[i]; });
          resultsMarkup = this.mergeResults(parts, searchTerm);
        }

        resultsMarkup = resultsMarkup.replace(/psearch/g, this.input.id);
        this.cachedResults[queryKey] = resultsMarkup;

        // Only render if this is still the latest query.
        if (this.latestRequestKey === queryKey) {
          this.renderedQuery = searchTerm;
          this.renderResults(resultsMarkup);
        }
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Merges the plain + scoped section responses into one dropdown.
     * @param {object} parts - { plain, products, used, packages } HTML strings.
     * @param {string} searchTerm - Search query (for the auto-selected tab).
     * @returns {string}
     */
    mergeResults(parts, searchTerm) {
      const docs = {};
      Object.keys(parts).forEach((key) => {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = parts[key] || '';
        docs[key] = tmpl.content;
      });

      const base = docs.plain.querySelector('.predictive-search')
        || docs.products.querySelector('.predictive-search')
        || docs.used.querySelector('.predictive-search')
        || docs.packages.querySelector('.predictive-search');
      if (!base) return parts.plain || '';

      const results = base.querySelector('.predictive-search__results');
      let tabbed = base.querySelector('tabbed-content');

      const scoped = [];
      ['products', 'used', 'packages'].forEach((key) => {
        const doc = docs[key];
        if (!doc) return;
        const button = doc.querySelector(`#psearch-${key}-tab`);
        const panel = doc.querySelector(`#psearch-${key}`);
        if (button && panel) scoped.push({ key, button, panel, doc });
      });

      if (scoped.length) {
        if (!tabbed) {
          tabbed = scoped[0].doc.querySelector('tabbed-content').cloneNode(true);
          tabbed.querySelector('[role="tablist"]').innerHTML = '';
          tabbed.querySelectorAll('[role="tabpanel"]').forEach((panel) => panel.remove());
          const noResults = base.querySelector('.predictive-search__no-results');
          if (noResults) noResults.remove();
          results.appendChild(tabbed);
        }

        const tablist = tabbed.querySelector('[role="tablist"]');
        scoped.forEach(({ button, panel }) => {
          tablist.appendChild(button);
          tabbed.appendChild(panel);
        });

        // Canonical tab order: Products, Used & Open Box, Packages, Collections, Articles & Pages.
        const rank = (el) => {
          const match = el.id.match(/^psearch-(.+?)(-tab)?$/);
          const index = match ? this.tabOrder.indexOf(match[1]) : -1;
          return index === -1 ? 99 : index;
        };
        [...tablist.children].sort((a, b) => rank(a) - rank(b)).forEach((el) => tablist.appendChild(el));
        [...tabbed.querySelectorAll(':scope > [role="tabpanel"]')]
          .sort((a, b) => rank(a) - rank(b))
          .forEach((el) => tabbed.appendChild(el));
      }

      let skuTab = null;
      if (tabbed) {
        const limit = parseInt(theme.settings.pSearchLimit, 10) || 10;
        const hrefOf = (li) => {
          const link = li.querySelector('a[href]');
          return link ? link.getAttribute('href').split('?')[0] : '';
        };

        // Best matches first: when only a few products contain every word typed (a specific product),
        // put them at the top of their tab even if sold out. Fewer than `limit` = specific search.
        const preciseItems = docs.precise ? [...docs.precise.querySelectorAll('li[data-dc-kind]')] : [];
        if (preciseItems.length && preciseItems.length < limit) {
          ['products', 'used', 'packages'].forEach((key) => {
            const list = tabbed.querySelector(`#psearch-${key} ul`);
            const mine = preciseItems.filter((li) => li.dataset.dcKind === key);
            if (!list || !mine.length) return;
            const hrefs = new Set(mine.map(hrefOf));
            list.querySelectorAll(':scope > li[data-dc-kind]').forEach((li) => {
              if (hrefs.has(hrefOf(li))) li.remove();
            });
            list.prepend(...mine);
            [...list.querySelectorAll(':scope > li[data-dc-kind]')].slice(limit).forEach((li) => li.remove());
          });
        }

        // A result whose variant SKU equals the typed text goes first, and its tab is selected.
        const skuHit = tabbed.querySelector('[role="tabpanel"] li[data-dc-sku-match]');
        if (skuHit) {
          skuHit.parentElement.prepend(skuHit);
          const panel = skuHit.closest('[role="tabpanel"]');
          skuTab = panel ? tabbed.querySelector(`[role="tab"][aria-controls="${panel.id}"]`) : null;
        }
      }

      if (tabbed) {
        // Auto-select the tab the customer's words ask for (or the one holding an exact SKU match),
        // otherwise the first one.
        const buttons = [...tabbed.querySelectorAll('[role="tab"]')];
        const intent = window.dcSearchIntent.detect(searchTerm);
        let activeButton = skuTab || buttons.find((b) => intent !== 'products' && b.id === `psearch-${intent}-tab`);
        if (!activeButton) [activeButton] = buttons;
        buttons.forEach((b) => {
          const isActive = b === activeButton;
          b.setAttribute('aria-selected', isActive ? 'true' : 'false');
          if (isActive) b.removeAttribute('tabindex');
          else b.setAttribute('tabindex', '-1');
          const panel = tabbed.querySelector(`#${b.getAttribute('aria-controls')}`);
          if (panel) panel.hidden = !isActive;
        });
      }

      // Fix the scroller modifier class now that we know what the dropdown contains.
      const scroller = base.querySelector('.predictive-search__scroller');
      const hasQueries = !!base.querySelector('.predictive-search__suggestions');
      if (scroller) {
        scroller.classList.remove('predictive-search__scroller--suggestions', 'predictive-search__scroller--suggestions-only');
        if (hasQueries) {
          scroller.classList.add(tabbed ? 'predictive-search__scroller--suggestions' : 'predictive-search__scroller--suggestions-only');
        }
      }

      // Nothing anywhere: make sure a no-results message is shown.
      if (!tabbed && !hasQueries && !base.querySelector('.predictive-search__no-results')) {
        const fallback = docs.products.querySelector('.predictive-search__no-results');
        if (fallback) results.appendChild(fallback);
      }

      return base.outerHTML;
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
